import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Ban, CheckCircle2, ExternalLink, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import RideCreateForm from './RideCreateForm';
import TransportMapLibre from './TransportMapLibre';
import { getBackendErrorMessage } from '../utils/toastError';
import { extractCoordsFromText, resolveMapLink, extractPlaceNameFromUrl, fetchRouteGeometry } from '../utils/transportHelper';
import useEscapeKey from '../hooks/useEscapeKey';

export default function TransportRideModal({ isOpen, onClose, autoLocateOnOpenToken = 0 }) {
  useEscapeKey(onClose, isOpen);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { socket, connected } = useSocket();
  const watchIdRef = useRef(null);
  const lastSentAtRef = useRef(0);
  const trackingActiveForReqIdRef = useRef(null);
  const [boostFires, setBoostFires] = useState('');
  const [boostError, setBoostError] = useState(null);
  const [isEditingLocations, setIsEditingLocations] = useState(false);
  const [fromOverrideText, setFromOverrideText] = useState('');
  const [toOverrideText, setToOverrideText] = useState('');
  const [fromOverrideCoords, setFromOverrideCoords] = useState(null);
  const [toOverrideCoords, setToOverrideCoords] = useState(null);
  const [locEditError, setLocEditError] = useState(null);
  const [liveDriver, setLiveDriver] = useState(null);
  const [liveRequester, setLiveRequester] = useState(null);
  const [liveDriverReceivedAtMs, setLiveDriverReceivedAtMs] = useState(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [trackingEnabled, setTrackingEnabled] = useState(false);
  const [quoteActive, setQuoteActive] = useState(false);

  const requestsQuery = useQuery({
    queryKey: ['transport-requests-ride-modal', user?.id],
    enabled: !!user?.id && isOpen,
    queryFn: async () => {
      const res = await axios.get('/api/transport/requests', { params: { kind: 'ride' } });
      return Array.isArray(res.data?.requests) ? res.data.requests : [];
    },
    refetchInterval: () => (quoteActive ? false : 8000),
    staleTime: 2000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  const activeReq = useMemo(() => {
    const list = Array.isArray(requestsQuery.data) ? requestsQuery.data : [];
    return (
      list.find(
        (r) =>
          r &&
          r.kind === 'ride' &&
          r.requester_user_id === user?.id &&
          ['open', 'accepted', 'delivered_pending'].includes(r.status)
      ) || null
    );
  }, [requestsQuery.data, user?.id]);

  const stopTracking = React.useCallback(() => {
    if (watchIdRef.current != null && navigator.geolocation) {
      try {
        navigator.geolocation.clearWatch(watchIdRef.current);
      } catch {
      }
    }
    watchIdRef.current = null;
    setTrackingEnabled(false);
    trackingActiveForReqIdRef.current = null;
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setLiveDriver(null);
      setLiveRequester(null);
      setLiveDriverReceivedAtMs(null);
      setQuoteActive(false);
      stopTracking();
      return;
    }
    setLiveDriver(null);
    setLiveRequester(null);
    setLiveDriverReceivedAtMs(null);
    if (activeReq?.id) {
      setQuoteActive(false);
    }
  }, [isOpen, activeReq?.id, stopTracking]);

  useEffect(() => {
    return () => {
      if (watchIdRef.current != null && navigator.geolocation) {
        try {
          navigator.geolocation.clearWatch(watchIdRef.current);
        } catch {
        }
      }
      watchIdRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    if (!liveDriverReceivedAtMs) return;
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [isOpen, liveDriverReceivedAtMs]);

  useEffect(() => {
    if (!socket || !isOpen) return;

    const onLocation = (payload) => {
      try {
        const requestId = payload?.request_id;
        const driver = payload?.driver;
        const requester = payload?.requester;
        if (!requestId) return;
        if (!activeReq?.id || requestId !== activeReq.id) return;

        if (driver) {
          setLiveDriver(driver);
          setLiveDriverReceivedAtMs(Date.now());
        }

        if (requester) {
          setLiveRequester(requester);
        }

        // Do not invalidate/poll on every location event; keep UI smooth.
      } catch {
        // ignore
      }
    };

    socket.on('transport:location', onLocation);
    return () => {
      socket.off('transport:location', onLocation);
    };
  }, [socket, isOpen, activeReq?.id, queryClient, user?.id]);

  const pricingSnap = activeReq?.pricing_snapshot && typeof activeReq.pricing_snapshot === 'object' ? activeReq.pricing_snapshot : {};
  const grossFires = Number.isFinite(Number(pricingSnap.gross_fare_fires))
    ? Number(pricingSnap.gross_fare_fires)
    : Number.isFinite(Number(activeReq?.fare_fires))
    ? Number(activeReq?.fare_fires)
    : null;
  const commissionFires = Number.isFinite(Number(pricingSnap.platform_commission_fires))
    ? Number(pricingSnap.platform_commission_fires)
    : null;
  const payoutFires = Number.isFinite(Number(pricingSnap.driver_payout_fires))
    ? Number(pricingSnap.driver_payout_fires)
    : null;
  const boostTotal = Number.isFinite(Number(pricingSnap.boost_fires_total)) ? Number(pricingSnap.boost_fires_total) : 0;

  const [routeGeometry, setRouteGeometry] = useState(null);

  useEffect(() => {
    if (!activeReq?.id || !isOpen) {
      setRouteGeometry(null);
      return;
    }
    if (activeReq.route_snapshot?.geometry) {
      setRouteGeometry(activeReq.route_snapshot.geometry);
    } else {
      setRouteGeometry(null);
    }
  }, [activeReq?.id, activeReq?.route_snapshot?.geometry, isOpen]);

  const extractPlaceLabel = (loc) => {
    const raw = String(loc?.label || loc?.address || loc?.link || '').trim();
    if (!raw) return 'N/D';
    if (raw.startsWith('http')) {
       return extractPlaceNameFromUrl(raw) || 'Ubicación';
    }
    return raw;
  };

  const fromCoords =
    (activeReq?.from_location?.coords?.lat != null ? activeReq.from_location.coords : null) ||
    extractCoordsFromText(activeReq?.from_location?.link || activeReq?.from_location?.address || activeReq?.from_location?.label) ||
    (activeReq?.route_snapshot?.from?.lat != null && activeReq?.route_snapshot?.from?.lon != null
      ? { lat: activeReq.route_snapshot.from.lat, lng: activeReq.route_snapshot.from.lon }
      : null);

  const toCoords =
    (activeReq?.to_location?.coords?.lat != null ? activeReq.to_location.coords : null) ||
    extractCoordsFromText(activeReq?.to_location?.link || activeReq?.to_location?.address || activeReq?.to_location?.label) ||
    (activeReq?.route_snapshot?.to?.lat != null && activeReq?.route_snapshot?.to?.lon != null
      ? { lat: activeReq.route_snapshot.to.lat, lng: activeReq.route_snapshot.to.lon }
      : null);

  useEffect(() => {
    if (!isOpen || !activeReq?.id) return;
    if (routeGeometry) return;
    if (!fromCoords || !toCoords) return;

    let cancelled = false;
    const load = async () => {
      const geo = await fetchRouteGeometry(fromCoords, toCoords);
      if (!cancelled && geo) {
        setRouteGeometry(geo);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, activeReq?.id, fromCoords, toCoords, routeGeometry]);




  useEffect(() => {
    if (!isOpen || !activeReq?.id) return;
    if (activeReq.to_location?.coords) return; // already resolved

    const toLink = activeReq.to_location?.link;
    if (toLink && /^https?:\/\//i.test(toLink)) {
      resolveMapLink(toLink).then(res => {
        if (res?.coords && res.coords.lat != null) {
          // We don't save to DB here to avoid side effects in a viewer, 
          // but we could set a local resolvedCoords if needed.
          // The useMemo for toCoords already tries to extract from link,
          // so if resolveMapLink expanded it, it might move to a format
          // extractCoordsFromText can read.
        }
      });
    }
  }, [isOpen, activeReq?.id, activeReq?.to_location?.link]);

  const effectiveFromCoords = fromOverrideCoords || fromCoords;
  const effectiveToCoords = toOverrideCoords || toCoords;

  const hasFrom = effectiveFromCoords && effectiveFromCoords.lat != null && effectiveFromCoords.lng != null;
  const hasTo = effectiveToCoords && effectiveToCoords.lat != null && effectiveToCoords.lng != null;

  const liveDriverCoords =
    liveDriver && liveDriver.lat != null && liveDriver.lon != null
      ? { lat: Number(liveDriver.lat), lng: Number(liveDriver.lon) }
      : null;
  const snapDriverCoords =
    activeReq?.route_snapshot?.driver?.lat != null && activeReq?.route_snapshot?.driver?.lon != null
      ? { lat: Number(activeReq.route_snapshot.driver.lat), lng: Number(activeReq.route_snapshot.driver.lon) }
      : null;
  const driverCoords = liveDriverCoords || snapDriverCoords;
  const hasDriver = driverCoords && Number.isFinite(driverCoords.lat) && Number.isFinite(driverCoords.lng);

  const requesterCoords =
    (liveRequester && liveRequester.lat != null && liveRequester.lon != null
      ? { lat: Number(liveRequester.lat), lng: Number(liveRequester.lon) }
      : null) ||
    (activeReq?.route_snapshot?.requester?.lat != null && activeReq?.route_snapshot?.requester?.lon != null
      ? { lat: Number(activeReq.route_snapshot.requester.lat), lng: Number(activeReq.route_snapshot.requester.lon) }
      : null);
  const hasRequester = requesterCoords && Number.isFinite(requesterCoords.lat) && Number.isFinite(requesterCoords.lng);

  const trackingPoints = useMemo(() => {
    const pts = [];
    if (hasFrom) pts.push([Number(effectiveFromCoords.lat), Number(effectiveFromCoords.lng)]);
    if (hasTo) pts.push([Number(effectiveToCoords.lat), Number(effectiveToCoords.lng)]);
    if (hasDriver) pts.push([Number(driverCoords.lat), Number(driverCoords.lng)]);
    if (hasRequester) pts.push([Number(requesterCoords.lat), Number(requesterCoords.lng)]);
    return pts;
  }, [hasFrom, hasTo, hasDriver, hasRequester, effectiveFromCoords, effectiveToCoords, driverCoords, requesterCoords]);

  const trackingCenter = useMemo(() => {
    if (trackingPoints.length > 0) return trackingPoints[trackingPoints.length - 1];
    return [10.4806, -66.9036];
  }, [trackingPoints]);

  const secondsSinceDriverUpdate = useMemo(() => {
    const ts = liveDriverReceivedAtMs;
    if (!ts) return null;
    return Math.max(0, Math.round((nowMs - ts) / 1000));
  }, [liveDriverReceivedAtMs, nowMs]);

  const locationMutation = useMutation({
    mutationFn: async ({ requestId, coords }) => {
      return axios.post(`/api/transport/${requestId}/location`, coords);
    },
  });

  const startTracking = () => {
    if (!activeReq?.id) return;
    if (!navigator.geolocation) {
      toast.error('Este dispositivo no soporta geolocalización.');
      return;
    }

    if (watchIdRef.current != null) {
      try {
        navigator.geolocation.clearWatch(watchIdRef.current);
      } catch {
      }
      watchIdRef.current = null;
    }

    lastSentAtRef.current = 0;
    setTrackingEnabled(true);
    trackingActiveForReqIdRef.current = activeReq.id;

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const now = Date.now();
        if (now - lastSentAtRef.current < 4000) return;
        lastSentAtRef.current = now;

        const coords = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          heading: pos.coords.heading,
          speed: pos.coords.speed,
        };
        locationMutation.mutate({ requestId: activeReq.id, coords });
      },
      (err) => {
        setTrackingEnabled(false);
        toast.error(getBackendErrorMessage(err, 'No se pudo obtener tu ubicación'));
      },
      {
        enableHighAccuracy: true,
        maximumAge: 2000,
        timeout: 15000,
      }
    );
  };

  useEffect(() => {
    if (!isOpen) {
      stopTracking();
      return;
    }

    const status = activeReq?.status;
    const shouldAutoTrack = !!activeReq?.id && ['accepted', 'delivered_pending'].includes(status);

    if (!shouldAutoTrack) {
      if (trackingEnabled) stopTracking();
      return;
    }

    if (trackingActiveForReqIdRef.current === activeReq.id && trackingEnabled) return;
    startTracking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeReq?.id, activeReq?.status, stopTracking, trackingEnabled]);

  const haversineKm = (a, b) => {
    const lat1 = a?.lat;
    const lon1 = a?.lng;
    const lat2 = b?.lat;
    const lon2 = b?.lng;
    if (!Number.isFinite(lat1) || !Number.isFinite(lon1) || !Number.isFinite(lat2) || !Number.isFinite(lon2)) return null;
    const toRad = (deg) => (deg * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const rLat1 = toRad(lat1);
    const rLat2 = toRad(lat2);
    const sinDLat = Math.sin(dLat / 2);
    const sinDLon = Math.sin(dLon / 2);
    const aVal = sinDLat * sinDLat + sinDLon * sinDLon * Math.cos(rLat1) * Math.cos(rLat2);
    const c = 2 * Math.atan2(Math.sqrt(aVal), Math.sqrt(1 - aVal));
    const d = R * c;
    if (!Number.isFinite(d) || d < 0) return null;
    return d;
  };

  const distanceDriverToDestinationKm = useMemo(() => {
    if (!hasDriver || !hasTo) return null;
    return haversineKm(driverCoords, effectiveToCoords);
  }, [hasDriver, hasTo, driverCoords, effectiveToCoords]);

  const driverNearDestination = distanceDriverToDestinationKm != null && distanceDriverToDestinationKm <= 0.15;

  const directionsLink =
    hasFrom && hasTo
      ? `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
          `${effectiveFromCoords.lat},${effectiveFromCoords.lng}`
        )}&destination=${encodeURIComponent(`${effectiveToCoords.lat},${effectiveToCoords.lng}`)}&travelmode=driving`
      : null;

  const applyLocationOverrides = () => {
    setLocEditError(null);
    const fromText = String(fromOverrideText || '').trim();
    const toText = String(toOverrideText || '').trim();
    const parsedFrom = fromText ? extractCoordsFromText(fromText) : null;
    const parsedTo = toText ? extractCoordsFromText(toText) : null;

    if (fromText && !parsedFrom) {
      setLocEditError('No pude extraer coordenadas del Origen (usa link de Google Maps o "lat,lng").');
      return;
    }
    if (toText && !parsedTo) {
      setLocEditError('No pude extraer coordenadas del Destino (usa link de Google Maps o "lat,lng").');
      return;
    }

    setFromOverrideCoords(parsedFrom);
    setToOverrideCoords(parsedTo);
    setIsEditingLocations(false);
  };

  const cancelMutation = useMutation({
    mutationFn: async (reqId) => axios.post(`/api/transport/${reqId}/cancel`),
    onSuccess: () => {
      queryClient.invalidateQueries(['transport-requests']);
      queryClient.invalidateQueries(['transport-requests-ride-layout']);
      queryClient.invalidateQueries(['transport-requests-ride-modal']);
    }
  });

  const doCancel = () => {
    if (!activeReq?.id) return;
    const ok = window.confirm('¿Seguro que quieres cancelar esta solicitud?');
    if (!ok) return;
    cancelMutation.mutate(activeReq.id);
  };

  const confirmMutation = useMutation({
    mutationFn: async (reqId) => axios.post(`/api/transport/${reqId}/confirm`),
    onSuccess: () => {
      queryClient.invalidateQueries(['transport-requests']);
      queryClient.invalidateQueries(['transport-requests-ride-layout']);
      queryClient.invalidateQueries(['transport-requests-ride-modal']);
      queryClient.invalidateQueries(['header-balance', user?.id]);
    }
  });

  const boostMutation = useMutation({
    mutationFn: async ({ reqId, addFires }) => axios.post(`/api/transport/${reqId}/boost`, { add_fires: addFires }),
    onSuccess: () => {
      queryClient.invalidateQueries(['transport-requests']);
      queryClient.invalidateQueries(['transport-requests-ride-layout']);
      queryClient.invalidateQueries(['transport-requests-ride-modal']);
      queryClient.invalidateQueries(['header-balance', user?.id]);
      setBoostFires('');
      setBoostError(null);
    }
  });

  const doBoost = async () => {
    setBoostError(null);
    const parsed = parseInt(String(boostFires || '').trim(), 10);
    if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed <= 0) {
      setBoostError('Solo se permiten números enteros (1,2,3...)');
      return;
    }
    if (!activeReq?.id) return;
    try {
      await boostMutation.mutateAsync({ reqId: activeReq.id, addFires: parsed });
    } catch (err) {
      setBoostError(getBackendErrorMessage(err, 'No se pudo aumentar la oferta'));
    }
  };

  const statusLabel =
    activeReq?.status === 'open'
      ? 'Abierta'
      : activeReq?.status === 'accepted'
      ? 'En camino'
      : activeReq?.status === 'delivered_pending'
      ? 'Llegada (pendiente)'
      : activeReq?.status === 'completed'
      ? 'Completada'
      : activeReq?.status === 'cancelled'
      ? 'Cancelada'
      : activeReq?.status || 'N/D';

  return !isOpen ? null : (
    <div className="fixed inset-0 z-[1500] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-[#0b1220] border border-slate-800 rounded-2xl w-full max-w-4xl max-h-[90vh] shadow-2xl shadow-black/50 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <div className="text-slate-100 font-semibold">Transporte</div>
            {activeReq ? (
              <span className="text-[11px] px-2 py-1 rounded-full bg-slate-800 text-slate-200 border border-slate-700">
                {statusLabel}
              </span>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-white text-sm px-2 py-1 rounded"
          >
            Cerrar
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3.5 md:p-4 space-y-3.5 md:space-y-4">
          {requestsQuery.isLoading ? <div className="text-sm text-slate-300">Cargando...</div> : null}
          {requestsQuery.isError ? (
            <div className="text-sm text-red-400">No se pudieron cargar tus solicitudes.</div>
          ) : null}

          {!activeReq ? (
            <RideCreateForm
              title="Solicitar transporte"
              autoLocateOnOpenToken={autoLocateOnOpenToken}
              onQuoteActiveChange={setQuoteActive}
              onCreated={() => {
                requestsQuery.refetch();
              }}
              storeId={user?.home_store_id || null}
            />
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="bg-slate-900 border border-slate-800 rounded-lg p-2.5 md:p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs text-slate-400">Origen / Destino</div>
                    <button
                      type="button"
                      onClick={() => {
                        const fromText = String(activeReq?.from_location?.link || activeReq?.from_location?.address || activeReq?.from_location?.label || '').trim();
                        const toText = String(activeReq?.to_location?.link || activeReq?.to_location?.address || activeReq?.to_location?.label || '').trim();
                        setFromOverrideText(fromText);
                        setToOverrideText(toText);
                        setLocEditError(null);
                        setIsEditingLocations((v) => !v);
                      }}
                      className="text-[11px] px-2 py-1 rounded bg-slate-800 text-slate-200 border border-slate-700 hover:border-blue-500/60"
                    >
                      {isEditingLocations ? 'Cerrar' : 'Editar'}
                    </button>
                  </div>

                  {!isEditingLocations ? (
                    <>
                      <div className="mt-2 text-[12px] md:text-sm text-slate-100 break-words">
                        <div className="text-[11px] text-slate-400">Origen</div>
                        {(() => {
                          const raw = String(activeReq?.from_location?.link || activeReq?.from_location?.address || activeReq?.from_location?.label || '').trim() || 'N/D';
                          const isUrl = raw.startsWith('http');
                          if (isUrl) {
                            const placeName = extractPlaceNameFromUrl(raw);
                            return (
                              <span className="inline-flex items-center gap-2 min-w-0">
                                <span className="break-words text-slate-100">{placeName || 'Ubicación'}</span>
                                <a
                                  href={raw}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-sky-300 hover:text-sky-200"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <ExternalLink size={14} />
                                  Link
                                </a>
                              </span>
                            );
                          }
                          return <span>{raw}</span>;
                        })()}
                      </div>

                      <div className="mt-2 text-[12px] md:text-sm text-slate-100 break-words">
                        <div className="text-[11px] text-slate-400">Destino</div>
                        {(() => {
                          const raw = String(activeReq?.to_location?.link || activeReq?.to_location?.address || activeReq?.to_location?.label || '').trim() || 'N/D';
                          const isUrl = raw.startsWith('http');
                          if (isUrl) {
                            const placeName = extractPlaceNameFromUrl(raw);
                            return (
                              <span className="inline-flex items-center gap-2 min-w-0">
                                <span className="break-words text-slate-100">{placeName || 'Ubicación'}</span>
                                <a
                                  href={raw}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-sky-300 hover:text-sky-200"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <ExternalLink size={14} />
                                  Link
                                </a>
                              </span>
                            );
                          }
                          return <span>{raw}</span>;
                        })()}
                      </div>
                    </>
                  ) : (
                    <div className="mt-3 space-y-2">
                      <div>
                        <div className="text-[11px] text-slate-400">Origen (link o lat,lng)</div>
                        <input
                          type="text"
                          value={fromOverrideText}
                          onChange={(e) => setFromOverrideText(e.target.value)}
                          className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-[12px] md:text-sm text-slate-100"
                          placeholder='https://maps.google.com/... o "10.123,-66.456"'
                        />
                      </div>
                      <div>
                        <div className="text-[11px] text-slate-400">Destino (link o lat,lng)</div>
                        <input
                          type="text"
                          value={toOverrideText}
                          onChange={(e) => setToOverrideText(e.target.value)}
                          className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-[12px] md:text-sm text-slate-100"
                          placeholder='https://maps.google.com/... o "10.123,-66.456"'
                        />
                      </div>

                      {locEditError ? <div className="text-xs text-red-400">{locEditError}</div> : null}

                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={applyLocationOverrides}
                          className="px-3 py-2 rounded-lg bg-blue-600 text-white text-[12px] md:text-sm font-semibold hover:bg-blue-500"
                        >
                          Aplicar
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setIsEditingLocations(false);
                            setLocEditError(null);
                          }}
                          className="px-3 py-2 rounded-lg bg-slate-800 text-slate-100 border border-slate-700 text-[12px] md:text-sm font-semibold hover:border-blue-500/60"
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setFromOverrideCoords(null);
                            setToOverrideCoords(null);
                            setLocEditError(null);
                            setIsEditingLocations(false);
                          }}
                          className="px-3 py-2 rounded-lg bg-slate-800 text-slate-100 border border-slate-700 text-[12px] md:text-sm font-semibold hover:border-blue-500/60"
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-slate-900 border border-slate-800 rounded-lg p-2.5 md:p-3">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-slate-400">Oferta</div>
                    <div className="text-xs text-slate-400">Boost: {Number(boostTotal || 0).toFixed(2)} 🔥</div>
                  </div>
                  <div className="mt-1 text-[16px] md:text-lg font-semibold text-slate-100">
                    {grossFires != null ? Number(grossFires || 0).toFixed(2) : 'N/D'} 🔥
                  </div>
                  <div className="mt-1 grid gap-0.5 text-[11px] text-slate-300/80">
                    {commissionFires != null ? (
                      <div>Comisión plataforma: {Number(commissionFires || 0).toFixed(2)} 🔥</div>
                    ) : null}
                    {payoutFires != null ? (
                      <div>Pago transportista: {Number(payoutFires || 0).toFixed(2)} 🔥</div>
                    ) : null}
                  </div>
                </div>

                <div className="bg-slate-900 border border-slate-800 rounded-lg p-2.5 md:p-3 space-y-2">
                  <div className="text-xs text-slate-400">Aumentar oferta (top-up)</div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        const current = parseInt(String(boostFires || '0').trim() || '0', 10);
                        const next = (Number.isFinite(current) ? current : 0) + 50;
                        setBoostFires(String(next));
                      }}
                      className="px-3 py-2 rounded-lg bg-slate-800 text-slate-100 border border-slate-700 text-[12px] md:text-sm font-semibold hover:border-blue-500/60"
                    >
                      +50
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const current = parseInt(String(boostFires || '0').trim() || '0', 10);
                        const next = (Number.isFinite(current) ? current : 0) + 100;
                        setBoostFires(String(next));
                      }}
                      className="px-3 py-2 rounded-lg bg-slate-800 text-slate-100 border border-slate-700 text-[12px] md:text-sm font-semibold hover:border-blue-500/60"
                    >
                      +100
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      min="0"
                      step="1"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={boostFires}
                      onChange={(e) => {
                        const next = e.target.value;
                        if (next === '') {
                          setBoostFires('');
                          return;
                        }
                        if (/^\d+$/.test(next)) {
                          setBoostFires(next);
                        }
                      }}
                      className="flex-1 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-[12px] md:text-sm text-slate-100"
                      placeholder="Ej: 10"
                    />
                    <button
                      type="button"
                      onClick={doBoost}
                      disabled={boostMutation.isLoading}
                      className="px-3 py-2 rounded-lg bg-orange-500 text-white text-[12px] md:text-sm font-semibold hover:bg-orange-400 disabled:opacity-60 inline-flex items-center gap-2"
                    >
                      <Plus size={16} />
                      🔥
                    </button>
                  </div>
                  {boostError ? <div className="text-xs text-red-400">{boostError}</div> : null}
                </div>

                <div className="flex flex-wrap gap-2">
                  {activeReq.status === 'delivered_pending' ? (
                    <button
                      type="button"
                      onClick={() => confirmMutation.mutate(activeReq.id)}
                      disabled={confirmMutation.isLoading}
                      className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 disabled:opacity-60 inline-flex items-center gap-2"
                    >
                      <CheckCircle2 size={16} />
                      Confirmo que llegó
                    </button>
                  ) : null}

                  {['open', 'accepted'].includes(activeReq.status) ? (
                    <button
                      type="button"
                      onClick={doCancel}
                      disabled={cancelMutation.isLoading}
                      className="px-4 py-2 rounded-lg bg-red-600/20 text-red-200 border border-red-600/40 text-sm font-semibold hover:bg-red-600/30 disabled:opacity-60 inline-flex items-center gap-2"
                    >
                      <Ban size={16} />
                      Cancelar
                    </button>
                  ) : null}

                  {directionsLink ? (
                    <a
                      href={directionsLink}
                      target="_blank"
                      rel="noreferrer"
                      className="px-4 py-2 rounded-lg bg-slate-800 text-slate-100 border border-slate-700 text-[12px] md:text-sm font-semibold hover:border-blue-500/60 inline-flex items-center gap-2"
                    >
                      <ExternalLink size={16} />
                      Abrir ruta
                    </a>
                  ) : null}
                </div>

                {activeReq?.status !== 'open' ? (
                  <div className="bg-slate-900 border border-slate-800 rounded-lg p-2.5 md:p-3">
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-slate-400">Tracking</div>
                      <div className="text-[11px] text-slate-300/80">
                        {connected ? 'Conectado' : 'Desconectado'}
                      </div>
                    </div>
                    <div className="mt-1 text-[11px] text-slate-300/80">
                      {hasDriver
                        ? secondsSinceDriverUpdate != null
                          ? `Última actualización hace ${secondsSinceDriverUpdate}s`
                          : 'Ubicación disponible'
                        : 'Sin ubicación en vivo todavía.'}
                    </div>
                    {driverNearDestination ? (
                      <div className="mt-1 text-[11px] text-emerald-300/90">
                        Cerca del destino
                      </div>
                    ) : null}
                    {hasDriver ? (
                      <div className="mt-1 font-mono text-[11px] text-slate-200">
                        {Number(driverCoords.lat).toFixed(5)}, {Number(driverCoords.lng).toFixed(5)}
                      </div>
                    ) : null}
                    {hasRequester ? (
                      <div className="mt-2">
                        <div className="text-[11px] text-slate-300/80">Tu ubicación</div>
                        <div className="font-mono text-[11px] text-slate-200">
                          {Number(requesterCoords.lat).toFixed(5)}, {Number(requesterCoords.lng).toFixed(5)}
                        </div>
                      </div>
                    ) : null}

                    <div className="mt-2 flex gap-2">
                      {!trackingEnabled ? (
                        <button
                          type="button"
                          onClick={startTracking}
                          disabled={locationMutation.isLoading || !['accepted', 'delivered_pending'].includes(activeReq.status)}
                          className="flex-1 px-3 py-2 rounded-lg bg-slate-800 text-slate-100 text-xs border border-slate-700 hover:bg-slate-700 disabled:opacity-60"
                        >
                          Activar mi tracking
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={stopTracking}
                          className="flex-1 px-3 py-2 rounded-lg bg-red-600/20 text-red-200 text-xs border border-red-600/40 hover:bg-red-600/30 disabled:opacity-60"
                        >
                          Detener tracking
                        </button>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden h-[360px] md:h-[520px]">
                {hasTo || hasFrom || hasDriver ? (
                  <TransportMapLibre
                    view={hasDriver ? 'driver' : 'route'}
                    fromCoords={effectiveFromCoords}
                    toCoords={effectiveToCoords}
                    driverCoords={driverCoords}
                    routeGeometry={routeGeometry}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400 text-sm px-4 text-center">
                    No hay coordenadas para mostrar el mapa.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="border-t border-slate-800 px-4 py-3 bg-[#0b1220]">
          <button
            type="button"
            onClick={onClose}
            className="w-full px-4 py-3 rounded-xl bg-slate-800 text-slate-100 border border-slate-700 text-sm font-semibold hover:border-blue-500/60"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
